import { useState, useEffect } from "react";

// Grid box colors from the app
const GRID_COLORS = [
  "#22c55e", // Green (Exercise)
  "#3b82f6", // Blue (Reading)
  "#f97316", // Orange (Meditation)
  "#ec4899", // Pink (Nutrition)
  "#8b5cf6", // Purple (default primary)
  "#06b6d4", // Cyan (Teal variant)
  "#ef4444", // Red
  "#84cc16", // Lime
];

interface GridBox {
  id: string;
  x: number;
  y: number;
  color: string;
  size: number;
  opacity: number;
  delay: number;
  duration: number;
  rotation: number;
  animationType: 'gentle' | 'slow' | 'drift';
}

// Predefined box configurations to eliminate flickering and ensure consistency
const PREDEFINED_BOXES: Omit<GridBox, 'id'>[] = [
  // Top area boxes
  { x: 8, y: 12, color: "#22c55e", size: 5, opacity: 0.25, delay: 1, duration: 4, rotation: 45, animationType: 'gentle' },
  { x: 15, y: 8, color: "#3b82f6", size: 8, opacity: 0.18, delay: 2.5, duration: 6, rotation: 120, animationType: 'slow' },
  { x: 25, y: 15, color: "#f97316", size: 6, opacity: 0.22, delay: 0.8, duration: 5, rotation: 78, animationType: 'drift' },
  { x: 88, y: 10, color: "#ec4899", size: 4, opacity: 0.28, delay: 3.2, duration: 4.5, rotation: 156, animationType: 'gentle' },
  { x: 82, y: 18, color: "#8b5cf6", size: 7, opacity: 0.20, delay: 1.8, duration: 7, rotation: 234, animationType: 'slow' },
  { x: 92, y: 25, color: "#06b6d4", size: 5, opacity: 0.24, delay: 4.1, duration: 5.5, rotation: 312, animationType: 'drift' },
  
  // Large boxes in top corners
  { x: 5, y: 5, color: "#ef4444", size: 14, opacity: 0.12, delay: 3.8, duration: 8, rotation: 67, animationType: 'slow' },
  { x: 88, y: 8, color: "#84cc16", size: 16, opacity: 0.10, delay: 6.2, duration: 9, rotation: 143, animationType: 'drift' },
  
  // Upper left and right areas
  { x: 12, y: 22, color: "#ef4444", size: 9, opacity: 0.16, delay: 5.5, duration: 6.5, rotation: 89, animationType: 'gentle' },
  { x: 6, y: 28, color: "#84cc16", size: 4, opacity: 0.30, delay: 2.1, duration: 4.8, rotation: 167, animationType: 'slow' },
  { x: 85, y: 32, color: "#22c55e", size: 6, opacity: 0.21, delay: 6.8, duration: 5.2, rotation: 245, animationType: 'drift' },
  { x: 90, y: 38, color: "#3b82f6", size: 8, opacity: 0.17, delay: 3.7, duration: 6.8, rotation: 323, animationType: 'gentle' },
  
  // Large boxes in upper sides
  { x: 2, y: 35, color: "#f97316", size: 18, opacity: 0.08, delay: 7.5, duration: 10, rotation: 234, animationType: 'gentle' },
  { x: 94, y: 30, color: "#ec4899", size: 15, opacity: 0.11, delay: 4.8, duration: 7.5, rotation: 312, animationType: 'slow' },
  
  // Left and right middle areas (avoiding center)
  { x: 8, y: 45, color: "#f97316", size: 5, opacity: 0.26, delay: 4.9, duration: 4.2, rotation: 45, animationType: 'slow' },
  { x: 18, y: 52, color: "#ec4899", size: 7, opacity: 0.19, delay: 1.4, duration: 7.5, rotation: 123, animationType: 'drift' },
  { x: 85, y: 48, color: "#8b5cf6", size: 6, opacity: 0.23, delay: 7.2, duration: 5.8, rotation: 201, animationType: 'gentle' },
  { x: 92, y: 55, color: "#06b6d4", size: 4, opacity: 0.27, delay: 2.6, duration: 4.6, rotation: 279, animationType: 'slow' },
  
  // Large boxes in middle sides
  { x: 5, y: 50, color: "#8b5cf6", size: 20, opacity: 0.07, delay: 9.1, duration: 11, rotation: 89, animationType: 'drift' },
  { x: 88, y: 52, color: "#06b6d4", size: 17, opacity: 0.09, delay: 5.7, duration: 8.5, rotation: 167, animationType: 'gentle' },
  
  // Lower areas
  { x: 10, y: 68, color: "#ef4444", size: 8, opacity: 0.18, delay: 8.1, duration: 6.2, rotation: 357, animationType: 'drift' },
  { x: 22, y: 72, color: "#84cc16", size: 5, opacity: 0.25, delay: 3.4, duration: 5.4, rotation: 78, animationType: 'gentle' },
  { x: 15, y: 82, color: "#22c55e", size: 6, opacity: 0.21, delay: 6.1, duration: 7.1, rotation: 156, animationType: 'slow' },
  { x: 88, y: 75, color: "#3b82f6", size: 7, opacity: 0.20, delay: 4.7, duration: 5.9, rotation: 234, animationType: 'drift' },
  { x: 82, y: 85, color: "#f97316", size: 4, opacity: 0.29, delay: 1.9, duration: 4.4, rotation: 312, animationType: 'gentle' },
  { x: 90, y: 78, color: "#ec4899", size: 9, opacity: 0.15, delay: 9.2, duration: 6.6, rotation: 45, animationType: 'slow' },
  
  // Large boxes in lower areas
  { x: 8, y: 75, color: "#22c55e", size: 19, opacity: 0.08, delay: 2.8, duration: 9.5, rotation: 201, animationType: 'slow' },
  { x: 85, y: 82, color: "#3b82f6", size: 13, opacity: 0.13, delay: 8.9, duration: 7.2, rotation: 279, animationType: 'drift' },
  
  // Bottom area
  { x: 25, y: 88, color: "#8b5cf6", size: 5, opacity: 0.24, delay: 5.8, duration: 5.1, rotation: 123, animationType: 'drift' },
  { x: 35, y: 92, color: "#06b6d4", size: 6, opacity: 0.22, delay: 2.3, duration: 6.4, rotation: 201, animationType: 'gentle' },
  { x: 78, y: 90, color: "#ef4444", size: 4, opacity: 0.26, delay: 7.5, duration: 4.7, rotation: 279, animationType: 'slow' },
  
  // Large boxes in bottom corners
  { x: 5, y: 90, color: "#84cc16", size: 16, opacity: 0.10, delay: 6.3, duration: 8.8, rotation: 45, animationType: 'gentle' },
  { x: 92, y: 88, color: "#ef4444", size: 14, opacity: 0.12, delay: 3.5, duration: 7.8, rotation: 123, animationType: 'slow' },
  
  // Scattered smaller boxes in safe areas
  { x: 5, y: 35, color: "#84cc16", size: 3, opacity: 0.32, delay: 8.8, duration: 3.8, rotation: 89, animationType: 'drift' },
  { x: 28, y: 25, color: "#22c55e", size: 4, opacity: 0.28, delay: 4.3, duration: 5.7, rotation: 167, animationType: 'gentle' },
  { x: 72, y: 28, color: "#3b82f6", size: 3, opacity: 0.31, delay: 6.6, duration: 4.1, rotation: 245, animationType: 'slow' },
  { x: 95, y: 42, color: "#f97316", size: 5, opacity: 0.23, delay: 1.7, duration: 6.9, rotation: 323, animationType: 'drift' },
  { x: 3, y: 58, color: "#ec4899", size: 4, opacity: 0.27, delay: 9.5, duration: 5.3, rotation: 45, animationType: 'gentle' },
  { x: 26, y: 65, color: "#8b5cf6", size: 6, opacity: 0.19, delay: 3.1, duration: 7.2, rotation: 123, animationType: 'slow' },
  { x: 75, y: 62, color: "#06b6d4", size: 3, opacity: 0.30, delay: 8.4, duration: 4.9, rotation: 201, animationType: 'drift' },
  
  // Additional corner and edge boxes
  { x: 32, y: 12, color: "#ef4444", size: 7, opacity: 0.17, delay: 5.2, duration: 6.1, rotation: 279, animationType: 'gentle' },
  { x: 68, y: 15, color: "#84cc16", size: 4, opacity: 0.25, delay: 2.8, duration: 4.3, rotation: 357, animationType: 'slow' },
  { x: 8, y: 75, color: "#22c55e", size: 8, opacity: 0.16, delay: 7.8, duration: 7.4, rotation: 78, animationType: 'drift' },
  { x: 92, y: 68, color: "#3b82f6", size: 5, opacity: 0.24, delay: 4.5, duration: 5.6, rotation: 156, animationType: 'gentle' },
  
  // More scattered elements for richness
  { x: 18, y: 38, color: "#f97316", size: 4, opacity: 0.26, delay: 6.3, duration: 4.8, rotation: 234, animationType: 'slow' },
  { x: 82, y: 42, color: "#ec4899", size: 6, opacity: 0.21, delay: 1.6, duration: 6.7, rotation: 312, animationType: 'drift' },
  { x: 12, y: 85, color: "#8b5cf6", size: 3, opacity: 0.29, delay: 9.1, duration: 3.9, rotation: 45, animationType: 'gentle' },
  { x: 88, y: 12, color: "#06b6d4", size: 7, opacity: 0.18, delay: 3.9, duration: 8.1, rotation: 123, animationType: 'slow' },
  
  // Final touches
  { x: 35, y: 78, color: "#ef4444", size: 5, opacity: 0.23, delay: 7.1, duration: 5.5, rotation: 201, animationType: 'drift' },
  { x: 65, y: 82, color: "#84cc16", size: 4, opacity: 0.27, delay: 2.4, duration: 4.6, rotation: 279, animationType: 'gentle' },
  { x: 5, y: 15, color: "#22c55e", size: 6, opacity: 0.20, delay: 8.7, duration: 6.3, rotation: 357, animationType: 'slow' },
  { x: 95, y: 85, color: "#3b82f6", size: 8, opacity: 0.15, delay: 4.8, duration: 7.6, rotation: 78, animationType: 'drift' },
  
  // Additional large impact boxes
  { x: 22, y: 30, color: "#ec4899", size: 12, opacity: 0.14, delay: 4.2, duration: 6.8, rotation: 167, animationType: 'gentle' },
  { x: 78, y: 35, color: "#8b5cf6", size: 15, opacity: 0.11, delay: 7.9, duration: 8.2, rotation: 245, animationType: 'slow' },
  { x: 15, y: 60, color: "#06b6d4", size: 11, opacity: 0.15, delay: 5.4, duration: 7.6, rotation: 89, animationType: 'drift' },
  { x: 85, y: 65, color: "#f97316", size: 13, opacity: 0.12, delay: 2.7, duration: 9.1, rotation: 312, animationType: 'gentle' },
];

export default function ScatteredGridBoxes() {
  const [gridBoxes, setGridBoxes] = useState<GridBox[]>([]);

  useEffect(() => {
    // Use predefined boxes to eliminate flickering and ensure consistency
    const boxes: GridBox[] = PREDEFINED_BOXES.map((box, index) => ({
      ...box,
      id: `box-${index}`,
    }));

    setGridBoxes(boxes);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Desktop boxes - hidden on mobile */}
      <div className="hidden md:block">
        {gridBoxes.map((box) => (
          <div
            key={box.id}
            className={`absolute hero-grid-box-${box.animationType}`}
            style={{
              left: `${box.x}%`,
              top: `${box.y}%`,
              width: `${box.size}px`,
              height: `${box.size}px`,
              backgroundColor: box.color,
              borderRadius: `${Math.max(1, Math.min(4, box.size * 0.15))}px`,
              '--delay': `${box.delay}s`,
              '--duration': `${box.duration}s`,
              '--initial-rotation': `${box.rotation}deg`,
            } as React.CSSProperties}
          />
        ))}
      </div>
      
      {/* Mobile-optimized layout - visible only on mobile */}
      <div className="block md:hidden">
        {/* Top area - minimal interference */}
        <div className="absolute top-8 left-6 w-2 h-2 bg-[#22c55e] hero-grid-box-gentle" style={{ 
          '--delay': '1s', 
          '--duration': '4s',
          '--initial-rotation': '45deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute top-12 right-8 w-3 h-3 bg-[#3b82f6] hero-grid-box-slow" style={{ 
          '--delay': '2.5s', 
          '--duration': '5s',
          '--initial-rotation': '120deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute top-20 left-12 w-2 h-2 bg-[#f97316] hero-grid-box-drift" style={{ 
          '--delay': '0.8s', 
          '--duration': '6s',
          '--initial-rotation': '78deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        
        {/* Side areas - keeping clear of center text */}
        <div className="absolute top-32 left-4 w-3 h-3 bg-[#ec4899] hero-grid-box-gentle" style={{ 
          '--delay': '3.2s', 
          '--duration': '4.5s',
          '--initial-rotation': '156deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute top-40 right-6 w-2 h-2 bg-[#8b5cf6] hero-grid-box-slow" style={{ 
          '--delay': '1.8s', 
          '--duration': '7s',
          '--initial-rotation': '234deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        
        {/* Lower middle sides - avoiding center text area */}
        <div className="absolute top-64 left-6 w-3 h-3 bg-[#06b6d4] hero-grid-box-drift" style={{ 
          '--delay': '4.1s', 
          '--duration': '5.5s',
          '--initial-rotation': '312deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute top-72 right-4 w-2 h-2 bg-[#ef4444] hero-grid-box-gentle" style={{ 
          '--delay': '5.5s', 
          '--duration': '6.5s',
          '--initial-rotation': '89deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        
        {/* Bottom area - below main content */}
        <div className="absolute bottom-32 left-8 w-4 h-4 bg-[#84cc16] hero-grid-box-slow" style={{ 
          '--delay': '2.1s', 
          '--duration': '4.8s',
          '--initial-rotation': '167deg',
          borderRadius: '1.5px'
        } as React.CSSProperties} />
        <div className="absolute bottom-24 right-10 w-3 h-3 bg-[#22c55e] hero-grid-box-drift" style={{ 
          '--delay': '6.8s', 
          '--duration': '5.2s',
          '--initial-rotation': '245deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute bottom-16 left-12 w-2 h-2 bg-[#3b82f6] hero-grid-box-gentle" style={{ 
          '--delay': '3.7s', 
          '--duration': '6.8s',
          '--initial-rotation': '323deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute bottom-12 right-6 w-3 h-3 bg-[#f97316] hero-grid-box-slow" style={{ 
          '--delay': '7.2s', 
          '--duration': '5.8s',
          '--initial-rotation': '201deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        
        {/* Corner accents - very subtle */}
        <div className="absolute top-6 right-4 w-2 h-2 bg-[#ec4899] hero-grid-box-drift" style={{ 
          '--delay': '8.1s', 
          '--duration': '6.2s',
          '--initial-rotation': '357deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
        <div className="absolute bottom-8 left-4 w-2 h-2 bg-[#8b5cf6] hero-grid-box-gentle" style={{ 
          '--delay': '4.9s', 
          '--duration': '4.2s',
          '--initial-rotation': '45deg',
          borderRadius: '1px'
        } as React.CSSProperties} />
      </div>
    </div>
  );
} 